import { Box, Divider, Stack, styled } from '@mui/material';
import { useProfileCompletion } from 'features/profile/hooks/useProfileCompletion';
import { memo } from 'react';
import { useFeatureEnabled } from 'shared/hooks';

import { CandidateViewModel } from '../../types';
import { ProfileCompletionBanner } from '../ProfileCompletionBanner';
import ProfileDeleteButton from '../ProfileDeleteButton';
import CommunicationPreferencesSection from './CommunicationPreferencesSection';
import ContactDetailsSection from './ContactDetailsSection';
import PersonalInformationSection from './PersonalInformationSection';
import ProfessionalDetailsSection from './ProfessionalDetailsSection';

export interface ProfileViewProps {
    profile: CandidateViewModel;
}

export const ProfileView = memo(function ProfileView({ profile }: ProfileViewProps) {
    const isRoleSpecialtyEnabled = useFeatureEnabled('RoleSpecialtyTaxonomy');
    const roleSpecialties = profile.roleSpecialtyPairs ?? [];

    // Completion counts the active "professional roles" representation for the tenant.
    const { percentage, tip } = useProfileCompletion({
        ...profile,
        roles: isRoleSpecialtyEnabled ? roleSpecialties : profile.roles
    });

    return (
        <Stack gap={ 3 }>
            { percentage < 100 && (
                <ProfileCompletionBanner
                    percentage={ percentage }
                    tip={ tip }
                />
            ) }

            <PersonalInformationSection
                firstName={ profile.firstName }
                lastName={ profile.lastName }
            />

            <StyledDivider />

            <ContactDetailsSection
                email={ profile.email }
                mobilePhone={ profile.mobilePhone }
                location={ profile.location }
            />

            <StyledDivider />

            <ProfessionalDetailsSection
                roles={ profile.roles }
                roleSpecialties={ roleSpecialties }
                skills={ profile.skills }
            />

            <StyledDivider />

            <CommunicationPreferencesSection
                preferences={ profile.communicationPreferences }
            />

            <StyledDivider />

            <Box>
                <ProfileDeleteButton />
            </Box>
        </Stack>
    );
});

const StyledDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(4, 0, 2)
}));

export default ProfileView;
